import type { LucideIcon } from "lucide-react";

// ── Navigation model ──────────────────────────────────────────────────────
// One config-driven sidebar renders every shell (client, consultant, the
// consultant's read-through of a client portal, auditor). A shell builds a
// NavModel; AppSidebar renders it and knows nothing about roles. The contract
// is docs/NAVIGATION.md section 2 - keep the two in step.

export type NavBadgeKey =
  | "actions" | "runningCheck" | "unresolvedFeedback" | "resilience"          // client
  | "improvement" | "aiSystems" | "submissions"                              // client (OES build)
  | "governance" | "bia" | "vulnerabilities" | "remediation" | "people" | "myTasks" // client (assurance uplift)
  | "dataDiscovery"                                                          // client (data discovery)
  | "managementSystem"                                                       // client (ISO management-system registers)
  | "clientsNeedingAction" | "openFindings" | "myActions"             // consultant (myActions also auditor)
  | "clientsPendingReview";                                           // auditor

export interface NavLock {
  kind: "tier";
  /** Minimum service tier level that unlocks the item (mirrors the backend FEATURE_MIN_TIER). */
  minTier: number;
  /** Feature name shown in the TierLockModal ("<feature> requires a higher tier"). */
  feature: string;
}

export interface NavItem {
  /** Stable identifier: used for active matching, the palette and persisted group state. */
  id: string;
  label: string;
  /** Shell-relative path ("/documents", "/consultant/billing"). Resolved through the LinkAdapter. */
  to?: string;
  /** Action items (never active); rendered as a button. */
  onSelect?: () => void;
  /** Top-level items only. 15px in nav rows. */
  icon?: LucideIcon;
  badge?: NavBadgeKey;
  /** Shown greyed with a Lock icon; a click opens the TierLockModal instead of navigating. */
  lock?: NavLock;
  /** Permission / add-on / enrolment gating resolves to hidden. */
  hidden?: boolean;
  /** Framework colour swatch (rendered instead of an icon). */
  dot?: string;
  /** Text next to a framework group, e.g. "72%" or "n/a". */
  chip?: string | null;
  /** Collapsible group (frameworks, task areas); open state is persisted per shell. */
  children?: NavItem[];
  /**
   * Whether a collapsible group starts open before the person has opened or closed it. Defaults
   * to true; the client's standards and task areas start closed, and the group holding the
   * current page is always open.
   */
  defaultOpen?: boolean;
  /**
   * A task area: its pages are shown as tabs above the page (nav/areas.ts) and, once
   * finalised, as the rows of a collapsible group in the sidebar (`children`), each with its
   * own badge.
   */
  tabs?: NavItem[];
  /**
   * Not relevant to this organisation yet (an optional register it has not
   * used, a regime that does not apply): left out of the sidebar and the tabs,
   * listed on the More tools page instead. Never access control - the page and
   * its API stay exactly as reachable as before.
   */
  deferred?: boolean;
  /** One line for the More tools page. */
  description?: string;
  /** Extra search terms for the command palette. */
  keywords?: string[];
  /** Extra path prefixes that count as active (e.g. Connections -> /m365, /google, /storage). */
  activeMatch?: string[];
  /**
   * Active only when the location is exactly `to` (a list page whose detail
   * pages have their own rows).
   * `activeMatch` prefixes still match as usual.
   */
  exact?: boolean;
}

export interface NavSection {
  id: string;
  label?: string;
  items: NavItem[];
}

/** Client / engagement context rendered above the sections (consultant and auditor shells). */
export interface NavContextBlock {
  initial: string;
  title: string;
  subtitle?: string;
  dots?: Array<{ color: string; label: string }>;
  score?: number | null;
  /** Absolute app path (not passed through the LinkAdapter). */
  backTo: string;
  backLabel: string;
}

export interface SetupChecklistItem {
  id: string;
  label: string;
  done: boolean;
  /** Shell-relative path of the page that completes the step. */
  to: string;
}

/** Client only: the "get set up" card shown to org admins while any step is incomplete. */
export interface SetupChecklist {
  title?: string;
  items: SetupChecklistItem[];
}

export type NavShellKey = "client" | "consultant" | "consultant-portal" | "auditor";

export type NavRoleTone = "client" | "consultant" | "auditor";

export interface NavHeader {
  title: string;
  subtitle?: string;
  logoUrl?: string | null;
  role?: { label: string; tone: NavRoleTone };
}

export interface NavModel {
  shellKey: NavShellKey;
  header: NavHeader;
  context?: NavContextBlock;
  sections: NavSection[];
  /** Client only, see docs/NAVIGATION.md section 4. Shown on the What's next page, not in the sidebar. */
  setup?: SetupChecklist;
  /**
   * Filled by finaliseNavModel (nav/areas.ts): the pages the sidebar leaves out -
   * deferred (not relevant yet) and locked (a higher service tier) - for the More
   * tools page, so none of them costs the sidebar a row.
   */
  catalogue?: NavCatalogue;
}

export interface NavCatalogueEntry {
  item: NavItem;
  /** The area or section it belongs to, e.g. "Risks & incidents". */
  area?: string;
}

export interface NavCatalogue {
  deferred: NavCatalogueEntry[];
  locked: NavCatalogueEntry[];
}

/**
 * What a palette row is, which decides the heading it is listed under: a page,
 * something to do (a page opened on its form), a page above the service tier
 * (opens the TierLockModal), one of the organisation's records (GET /search) or
 * a shell extra such as the consultant's clients.
 */
export type PaletteEntryKind = "page" | "action" | "locked" | "record" | "client";

/** One entry of the command palette (the top bar's search box, or Ctrl/Cmd+K). */
export interface PaletteEntry {
  id: string;
  label: string;
  /** Section / group hint shown next to the label. */
  hint?: string;
  keywords?: string[];
  /** The page's one line from the nav model; searched, and shown for actions and locked pages. */
  description?: string;
  /** Defaults to "page". */
  kind?: PaletteEntryKind;
  /** A short tag at the end of the row: "Risk", "Supplier", "Tier 3". */
  tag?: string;
  /** Absolute app path (already resolved through the shell's adapter). */
  to?: string;
  onSelect?: () => void;
}

/** A workspace switch offered in the user menu (consultant workspace, auditor portal, own-company portal). */
export interface WorkspaceSwitch {
  id: string;
  label: string;
  description?: string;
  onSelect: () => void;
}

/**
 * Depth-first walk over the visible items of a model (groups and area tabs
 * included, hidden items skipped). Deferred pages are included: the command
 * palette still finds a module the sidebar has filed under More tools.
 */
export function visibleNavItems(model: NavModel): Array<{ item: NavItem; section: NavSection; group?: NavItem }> {
  const out: Array<{ item: NavItem; section: NavSection; group?: NavItem }> = [];
  for (const section of model.sections) {
    for (const item of section.items) {
      if (item.hidden) continue;
      out.push({ item, section });
      for (const child of item.children ?? item.tabs ?? []) {
        if (child.hidden) continue;
        out.push({ item: child, section, group: item });
      }
    }
  }
  for (const { item } of model.catalogue?.deferred ?? []) {
    if (!out.some((e) => e.item.id === item.id)) out.push({ item, section: { id: "more-tools", label: "More tools", items: [] } });
  }
  return out;
}
